import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { getAbsoluteDate } from 'common/utils/formatDate';
import { IColor, TColorKeys } from 'theme/palette';

interface IProps {
    color?: TColorKeys;
    date: string;
    textColor?: TColorKeys;
    textColorVariant?: keyof IColor;
    size?: 'small';
}

export const ListItemMediaDate = ({
    color,
    date,
    textColor,
    textColorVariant = 'contrastText',
    size,
}: IProps) => {
    const isSmall = size === 'small';
    const theme = useTheme();
    const themeColor = theme.vars.palette[color || 'primary'];
    const themeTextColor = theme.vars.palette[textColor || 'primary'];

    const isSameYear =
        new Date(date).getFullYear() === new Date().getFullYear();

    return (
        <Box
            component={motion.div}
            sx={{
                ...(isSmall && { width: '50%' }),
                alignSelf: 'stretch',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                borderTopLeftRadius: '4px',
                borderBottomLeftRadius: '4px',
                backgroundColor: { xs: themeColor?.main },
            }}
            transition={{ duration: 0.2 }}
            variants={{
                initListItemButton: {
                    backgroundColor: themeColor?.main,
                },
                hoverListItemButton: {
                    backgroundColor: themeColor?.accent,
                    transition: { duration: 0.2 },
                },
            }}
        >
            <Box
                component={motion.div}
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: themeTextColor[textColorVariant],
                    '& .MuiTypography-root': { color: 'inherit' },
                }}
                transition={{ type: 'easeOut' }}
                variants={{
                    initListItemButton: {
                        scale: 1,
                    },
                    hoverListItemButton: {
                        scale: 1.1,
                        transition: { type: 'easeIn' },
                    },
                }}
            >
                <Typography
                    sx={{
                        textTransform: 'uppercase',
                        fontSize: '1.2rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        top: '-2px',
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: { xs: '3rem', sm: '3.5rem' },
                            lineHeight: 1,
                            fontWeight: 700,
                        }}
                    >
                        {getAbsoluteDate(date, 'd')}
                    </Typography>

                    <Typography
                        component="span"
                        sx={{
                            lineHeight: 1.4,
                            fontSize: '1.2rem',
                            fontWeight: 700,
                        }}
                    >
                        {getAbsoluteDate(date, 'MMM')}
                    </Typography>

                    {!isSameYear && (
                        <Typography
                            component="span"
                            sx={{
                                lineHeight: 1,
                                fontWeight: 700,
                                fontSize: '1.2rem',
                            }}
                        >
                            {getAbsoluteDate(date, 'yyyy')}
                        </Typography>
                    )}
                </Typography>
            </Box>
        </Box>
    );
};
